import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    ArrowDownAZ,
    ArrowUpAZ,
    Download,
    Eye,
    ExternalLink,
    File,
    FileAudio,
    FileCode,
    FileSpreadsheet,
    FileText,
    FileVideo,
    Image,
    Loader2,
    Pencil,
    RefreshCw,
    Trash2,
    Upload,
    X
} from 'lucide-react';
import EmailPreviewContent from './EmailPreviewContent';
import { Checkbox } from './FieldComponents';
import { SearchBar } from './Nav';
import { withCsrfHeaders } from '../utils/csrf';
import { beginDownloadNotification, markDownloadNotificationStarted } from '../utils/downloadNotifications';
import '../styles/Documents.css';
import '../styles/Library.css';
import '../styles/LeadDocuments.css';

const LARGE_FILE_PREVIEW_LIMIT_BYTES = 500 * 1024 * 1024;
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'avi', 'm4v'];
const OFFICE_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
const TEXT_EXTENSIONS = ['txt', 'log', 'md', 'json', 'xml', 'csv', 'html', 'htm'];

const getFileName = (file) => file?.filename || file?.name || 'Untitled file';

const getDocumentKey = (file) => {
    if (!file) return '';
    if (file.row_key) return String(file.row_key);
    const source = file.source || (file.file_id ? 'files' : 'documents');
    const id = file.id || file.file_id || file.document_id;
    return id ? `${source}:${id}` : '';
};

const getDocumentRecordId = (file) => Number(file?.id || file?.file_id || file?.document_id || 0);
const getDocumentSource = (file) => file?.source || (file?.file_id ? 'files' : 'documents');
const getFileViewerId = (file) => {
    if (!file) return 0;
    if (file.file_id) return Number(file.file_id);
    if (file.file?.id) return Number(file.file.id);
    return getDocumentSource(file) === 'files' ? Number(file.id || 0) : 0;
};

const getFileExtension = (file) => {
    const fileName = getFileName(file);
    if (!fileName.includes('.')) {
        return '';
    }

    return fileName.split('.').pop().toLowerCase();
};

const formatFileSize = (bytes) => {
    const size = Number(bytes || 0);
    if (!Number.isFinite(size) || size <= 0) return '0 bytes';
    if (size < 1024) return `${size} bytes`;
    if (size < 1048576) return `${(size / 1024).toFixed(2)} KB`;
    if (size < 1073741824) return `${(size / 1048576).toFixed(2)} MB`;
    return `${(size / 1073741824).toFixed(2)} GB`;
};

const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Unknown date';

    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    }).format(date);
};

const getFileIcon = (fileType, fileName, size = 36) => {
    if (fileType) {
        if (fileType.includes('image/')) return <Image size={size} />;
        if (fileType.includes('text/javascript') || fileType.includes('application/json') || fileType.includes('text/html')) return <FileCode size={size} />;
        if (fileType.includes('text/csv') || fileType.includes('application/vnd.ms-excel') || fileType.includes('spreadsheet')) return <FileSpreadsheet size={size} />;
        if (fileType.includes('audio/')) return <FileAudio size={size} />;
        if (fileType.includes('video/')) return <FileVideo size={size} />;
        if (fileType.includes('application/pdf') || fileType.includes('message/')) return <FileText size={size} />;
    }

    const ext = String(fileName || '').split('.').pop().toLowerCase();
    if (IMAGE_EXTENSIONS.includes(ext)) return <Image size={size} />;
    if (['pdf', 'eml', 'msg'].includes(ext)) return <FileText size={size} />;
    if (['js', 'ts', 'json', 'html', 'css', 'php', 'xml'].includes(ext)) return <FileCode size={size} />;
    if (['csv', 'xlsx', 'xls'].includes(ext)) return <FileSpreadsheet size={size} />;
    if (AUDIO_EXTENSIONS.includes(ext)) return <FileAudio size={size} />;
    if (VIDEO_EXTENSIONS.includes(ext)) return <FileVideo size={size} />;
    return <File size={size} />;
};

const LeadPreviewPanel = ({ previewState, file, onView, onDownload, onDelete }) => {
    if (!previewState || !file) {
        return null;
    }

    const renderActions = () => (
        <div className="lead-preview-actions">
            <button type="button" className="action no-size" onClick={() => onView(file)}>
                <ExternalLink size={16} /> {previewState?.kind === 'email' ? 'Full Preview' : 'View file'}
            </button>
            <button type="button" className="action alt no-size" onClick={() => onDownload(file)}>
                <Download size={16} /> Download
            </button>
            <button type="button" className="action alt no-size danger" onClick={() => onDelete(file)}>
                <Trash2 size={16} /> Delete
            </button>
        </div>
    );

    switch (previewState.kind) {
        case 'loading':
            return (
                <div className="lead-preview-state centered">
                    <Loader2 className="animate-spin" size={24} />
                    <p>Loading preview…</p>
                </div>
            );

        case 'large':
            return (
                <div className="lead-preview-state">
                    <div className="lead-preview-meta">
                        <p>This file is too large for inline preview.</p>
                        <p>{formatFileSize(file.file_size)} exceeds the 500 MB inline preview limit.</p>
                    </div>
                    {renderActions()}
                </div>
            );

        case 'pdf':
            return (
                <div className="lead-preview-state media">
                    <iframe src={previewState.url} title={getFileName(file)} className="lead-preview-frame" />
                    {renderActions()}
                </div>
            );

        case 'image':
            return (
                <div className="lead-preview-state media">
                    <img src={previewState.url} alt={getFileName(file)} className="lead-preview-image" />
                    {renderActions()}
                </div>
            );

        case 'audio':
            return (
                <div className="lead-preview-state">
                    <audio controls src={previewState.url} className="lead-preview-audio" preload="metadata" />
                    {renderActions()}
                </div>
            );

        case 'video':
            return (
                <div className="lead-preview-state media">
                    <video controls src={previewState.url} className="lead-preview-video" preload="metadata" />
                    {renderActions()}
                </div>
            );

        case 'office':
            const leadOfficePreviewUrl = previewState.officePreviewUrl || previewState.office_preview_url || previewState.url;
            return (
                <div className="lead-preview-state media">
                    <iframe
                        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(leadOfficePreviewUrl)}`}
                        title={getFileName(file)}
                        className="lead-preview-frame"
                    />
                    {renderActions()}
                </div>
            );

        case 'text':
        case 'calendar':
            return (
                <div className="lead-preview-state">
                    <pre className="lead-preview-text">{previewState.text}</pre>
                    {previewState.truncated && (
                        <p className="lead-preview-note">Preview truncated. Open the file for the full contents.</p>
                    )}
                    {renderActions()}
                </div>
            );

        case 'email':
            return (
                <div className="lead-preview-state">
                    <div className="lead-email-preview">
                        <EmailPreviewContent
                            preview={previewState.preview}
                            fallbackTitle={getFileName(file)}
                            containerStyle={{ border: 'none', padding: 0, backgroundColor: 'transparent' }}
                            dividerStyle={{ margin: '12px 0' }}
                            textClassName="lead-preview-text"
                        />
                    </div>
                    {renderActions()}
                </div>
            );

        case 'generic':
            return (
                <div className="lead-preview-state">
                    <div className="lead-preview-meta">
                        <p><strong>Name:</strong> {getFileName(file)}</p>
                        <p><strong>Type:</strong> {file.file_type || getFileExtension(file) || 'Unknown'}</p>
                        <p><strong>Size:</strong> {formatFileSize(file.file_size)}</p>
                        <p><strong>Uploaded:</strong> {formatDate(file.upload_date)}</p>
                    </div>
                    {renderActions()}
                </div>
            );

        case 'error':
            return (
                <div className="lead-preview-state">
                    <div className="lead-preview-meta error">
                        <p>Unable to preview this file.</p>
                        <p>{previewState.message}</p>
                    </div>
                    {renderActions()}
                </div>
            );

        default:
            return null;
    }
};

export const LeadDocuments = ({ user_id, lead_id, metaData, fetchDocuments }) => {
    const [error, setError] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortColumn, setSortColumn] = useState('upload_date');
    const [sortDirection, setSortDirection] = useState('desc');
    const [selectedIds, setSelectedIds] = useState([]);
    const [activeFile, setActiveFile] = useState([]);
    const [sidePreview, setSidePreview] = useState(null);
    const [sidePreviewFile, setSidePreviewFile] = useState(null);
    const [actionFileId, setActionFileId] = useState(null);
    const [more, setMore] = useState(null);
    const [menuPositionAbove, setMenuPositionAbove] = useState({});
    const [menuPosition, setMenuPosition] = useState({});
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1440);
    const [isDragOver, setIsDragOver] = useState(false);
    const [dragCounter, setDragCounter] = useState(0);
    const clickTimer = useRef(null);
    const menuRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!sidePreviewFile || !Array.isArray(metaData)) {
            return;
        }

        const currentKey = getDocumentKey(sidePreviewFile);
        const updatedFile = metaData.find((entry) => getDocumentKey(entry) === currentKey);
        if (!updatedFile) {
            setSidePreview(null);
            setSidePreviewFile(null);
            return;
        }

        setSidePreviewFile(updatedFile);
    }, [metaData, sidePreviewFile]);

    useEffect(() => {
        if (!Array.isArray(metaData)) {
            setSelectedIds([]);
            setActiveFile([]);
            return;
        }

        const validIds = new Set(metaData.map((entry) => getDocumentKey(entry)).filter(Boolean));
        setSelectedIds((current) => current.filter((id) => validIds.has(String(id))));
        setActiveFile((current) => current.filter((id) => validIds.has(String(id))));
    }, [metaData]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleDocumentClick = (event) => {
            const target = event.target;
            if (menuRef.current && menuRef.current.contains(target)) return;
            if (target?.closest?.('.file-actions-dots')) return;
            setMore(null);
        };

        document.addEventListener('mousedown', handleDocumentClick);
        return () => document.removeEventListener('mousedown', handleDocumentClick);
    }, []);

    useEffect(() => {
        if (more !== null && menuRef.current) {
            const position = calculateMenuPosition(more, menuRef.current);
            setMenuPositionAbove((prev) => ({ ...prev, [more]: position.showAbove }));
            setMenuPosition((prev) => ({ ...prev, [more]: { left: position.left, top: position.top } }));
        }
    }, [more, windowWidth]);

    const filteredDocuments = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        const documents = Array.isArray(metaData) ? [...metaData] : [];

        const filtered = query
            ? documents.filter((file) => {
                const haystack = [
                    getFileName(file),
                    file?.file_type,
                    formatDate(file?.upload_date)
                ].filter(Boolean).join(' ').toLowerCase();

                return haystack.includes(query);
            })
            : documents;

        return filtered.sort((left, right) => {
            let comparison = 0;

            if (sortColumn === 'name') {
                comparison = getFileName(left).localeCompare(getFileName(right));
            } else if (sortColumn === 'size') {
                comparison = Number(left?.file_size || 0) - Number(right?.file_size || 0);
            } else if (sortColumn === 'type') {
                comparison = String(left?.file_type || getFileExtension(left) || '').localeCompare(String(right?.file_type || getFileExtension(right) || ''));
            } else {
                comparison = new Date(left?.upload_date || 0).getTime() - new Date(right?.upload_date || 0).getTime();
            }

            return sortDirection === 'asc' ? comparison : -comparison;
        });
    }, [metaData, searchQuery, sortColumn, sortDirection]);

    const selectedFiles = useMemo(
        () => filteredDocuments.filter((file) => selectedIds.includes(getDocumentKey(file))),
        [filteredDocuments, selectedIds]
    );

    const visibleSelectableIds = useMemo(
        () => filteredDocuments.map((file) => getDocumentKey(file)).filter(Boolean),
        [filteredDocuments]
    );

    const calculateMenuPosition = (menuId, menuElement = null) => {
        const dotsElement = document.querySelector(`[data-menu-index="${menuId}"]`);
        if (!dotsElement) {
            return { left: 0, top: 0, showAbove: false };
        }

        const dotsRect = dotsElement.getBoundingClientRect();
        const menuHeight = menuElement ? menuElement.offsetHeight : 220;
        const menuWidth = menuElement ? menuElement.offsetWidth : 170;
        const spaceBelow = window.innerHeight - dotsRect.bottom - 10;
        const showAbove = spaceBelow < menuHeight;

        let left = dotsRect.right - menuWidth;
        let top = showAbove ? dotsRect.top - menuHeight - 5 : dotsRect.bottom + 5;

        if (left < 10) left = 10;
        if (left + menuWidth > window.innerWidth - 10) left = window.innerWidth - menuWidth - 10;
        if (top < 10) top = 10;
        if (top + menuHeight > window.innerHeight - 10) top = window.innerHeight - menuHeight - 10;

        return { left, top, showAbove };
    };

    const handleDownload = (file) => {
        if (!file?.download_url) {
            setError('This file does not have a download URL yet. Refresh and try again.');
            return;
        }

        const notificationId = beginDownloadNotification(file?.file_name || file?.name);
        window.open(file.download_url, '_blank', 'noopener,noreferrer');
        markDownloadNotificationStarted(notificationId);
    };

    const handleViewFile = (file) => {
        const fileViewerId = getFileViewerId(file);
        if (fileViewerId > 0) {
            window.open(`/file/${fileViewerId}`, '_blank', 'noopener,noreferrer');
            return;
        }

        if (file?.download_url) {
            window.open(file.download_url, '_blank', 'noopener,noreferrer');
            return;
        }

        setError('Unable to open this document because no files record is available yet.');
    };

    const handleRefresh = async () => {
        setError(null);
        await fetchDocuments();
    };

    const uploadFiles = async (files) => {
        if (!lead_id) {
            setError('Missing lead context for upload. Refresh and try again.');
            return;
        }

        if (!Array.isArray(files) || files.length === 0) {
            return;
        }

        setUploading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('lead_id', lead_id);
            if (user_id) {
                formData.append('user_id', user_id);
            }

            files.forEach((file, index) => {
                formData.append('file[]', file);
            });

            const response = await fetch('https://api.casedb.co/lead_documents.php', {
                method: 'POST',
                headers: withCsrfHeaders({}, 'POST'),
                body: formData,
                credentials: 'include'
            });

            const result = await response.json();
            if (!response.ok || !result?.success) {
                throw new Error(result?.message || result?.error || 'Upload failed');
            }

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            await fetchDocuments();
        } catch (uploadError) {
            setError(`Upload error: ${uploadError.message}`);
        } finally {
            setUploading(false);
        }
    };

    const traverseDirectory = (directoryEntry) => new Promise((resolve) => {
        const files = [];
        const reader = directoryEntry.createReader();

        const readEntries = () => {
            reader.readEntries(async (entries) => {
                if (!entries.length) {
                    resolve(files);
                    return;
                }

                for (const entry of entries) {
                    if (entry.isDirectory) {
                        const nestedFiles = await traverseDirectory(entry);
                        files.push(...nestedFiles);
                    } else if (entry.isFile) {
                        const file = await new Promise((fileResolve) => entry.file(fileResolve));
                        if (file) {
                            files.push(file);
                        }
                    }
                }

                readEntries();
            });
        };

        readEntries();
    });

    const handleDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragCounter((current) => current + 1);

        if (event.dataTransfer?.items?.length) {
            const hasFiles = Array.from(event.dataTransfer.items).some((item) => item.kind === 'file');
            if (hasFiles) {
                setIsDragOver(true);
            }
        }
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();

        setDragCounter((current) => {
            const next = current - 1;
            if (next <= 0) {
                setIsDragOver(false);
                return 0;
            }
            return next;
        });
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
        }
    };

    const handleDrop = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);
        setDragCounter(0);

        const items = Array.from(event.dataTransfer?.items || []);
        const droppedFiles = [];

        try {
            if (items.length > 0) {
                for (const item of items) {
                    if (item.kind !== 'file') {
                        continue;
                    }

                    const entry = item.webkitGetAsEntry?.();
                    if (entry?.isDirectory) {
                        const nestedFiles = await traverseDirectory(entry);
                        droppedFiles.push(...nestedFiles);
                    } else {
                        const file = item.getAsFile?.();
                        if (file) {
                            droppedFiles.push(file);
                        }
                    }
                }
            } else if (event.dataTransfer?.files?.length) {
                droppedFiles.push(...Array.from(event.dataTransfer.files));
            }

            await uploadFiles(droppedFiles);
        } catch (dropError) {
            setError(`Upload error: ${dropError.message}`);
        }
    };

    const handleButtonDrop = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.classList.remove('drag-over');

        const files = Array.from(event.dataTransfer?.files || []);
        await uploadFiles(files);
    };

    const handleFileInputChange = async (event) => {
        const files = Array.from(event.target.files || []);
        await uploadFiles(files);
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection(column === 'upload_date' ? 'desc' : 'asc');
        }
    };

    const handleEditDocument = async (file) => {
        if (!file?.id) {
            setError('Unable to edit this document because no document ID is available.');
            return;
        }

        const nextType = window.prompt('Document type', file.document_type || '');
        if (nextType === null) {
            return;
        }

        const nextDescription = window.prompt('Description', file.description || '');
        if (nextDescription === null) {
            return;
        }

        setActionFileId(getDocumentKey(file));
        setError(null);

        try {
            const response = await fetch('https://api.casedb.co/lead_documents.php', {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    document_id: getDocumentRecordId(file),
                    source: getDocumentSource(file),
                    document_type: nextType,
                    description: nextDescription
                })
            });

            const result = await response.json();
            if (!response.ok || !result?.success) {
                throw new Error(result?.message || result?.error || 'Update failed');
            }

            await fetchDocuments();
        } catch (updateError) {
            setError(`Update error: ${updateError.message}`);
        } finally {
            setActionFileId(null);
        }
    };

    const toggleFileSelection = (fileId) => {
        const documentKey = String(fileId || '');
        if (!documentKey) return;
        setSelectedIds((current) => (
            current.includes(documentKey)
                ? current.filter((id) => id !== documentKey)
                : [...current, documentKey]
        ));
    };

    const toggleSelectAllVisible = () => {
        const allVisibleSelected = visibleSelectableIds.length > 0 && visibleSelectableIds.every((id) => selectedIds.includes(id));

        if (allVisibleSelected) {
            setSelectedIds((current) => current.filter((id) => !visibleSelectableIds.includes(String(id))));
            return;
        }

        setSelectedIds((current) => Array.from(new Set([...current, ...visibleSelectableIds])));
    };

    const clearSelection = () => {
        setSelectedIds([]);
    };

    const handleBulkDownload = () => {
        if (selectedFiles.length === 0) {
            setError('Select at least one document to download.');
            return;
        }

        selectedFiles.forEach((file, index) => {
            if (!file?.download_url) {
                return;
            }

            window.setTimeout(() => {
                const link = document.createElement('a');
                link.href = file.download_url;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.download = getFileName(file);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }, index * 150);
        });
    };

    const handleDeleteDocument = async (file) => {
        const documentId = getDocumentRecordId(file);
        if (!documentId) {
            setError('Unable to delete this file because no document ID is available.');
            return;
        }

        const confirmed = window.confirm(`Delete ${getFileName(file)}?`);
        if (!confirmed) {
            return;
        }

        setActionFileId(getDocumentKey(file));
        setError(null);

        try {
            const response = await fetch('https://api.casedb.co/lead_documents.php', {
                method: 'DELETE',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ document_refs: [{ id: documentId, source: getDocumentSource(file) }] })
            });

            const result = await response.json();
            if (!response.ok || !result?.success) {
                throw new Error(result?.message || result?.error || 'Delete failed');
            }

            if (getDocumentKey(sidePreviewFile) === getDocumentKey(file)) {
                setSidePreview(null);
                setSidePreviewFile(null);
            }

            await fetchDocuments();
        } catch (deleteError) {
            setError(`Delete error: ${deleteError.message}`);
        } finally {
            setActionFileId(null);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) {
            setError('Select at least one document to delete.');
            return;
        }

        const confirmed = window.confirm(`Delete ${selectedIds.length} selected document${selectedIds.length === 1 ? '' : 's'}?`);
        if (!confirmed) {
            return;
        }

        setActionFileId('bulk-delete');
        setError(null);

        try {
            const response = await fetch('https://api.casedb.co/lead_documents.php', {
                method: 'DELETE',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    document_refs: selectedFiles.map((file) => ({
                        id: getDocumentRecordId(file),
                        source: getDocumentSource(file)
                    }))
                })
            });

            const result = await response.json();
            if (!response.ok || !result?.success) {
                throw new Error(result?.message || result?.error || 'Bulk delete failed');
            }

            if (selectedIds.includes(getDocumentKey(sidePreviewFile))) {
                setSidePreview(null);
                setSidePreviewFile(null);
            }

            setSelectedIds([]);
            await fetchDocuments();
        } catch (deleteError) {
            setError(`Delete error: ${deleteError.message}`);
        } finally {
            setActionFileId(null);
        }
    };

    const buildTextPreview = async (file, kind = 'text') => {
        const response = await fetch(file.download_url);
        if (!response.ok) {
            throw new Error(`Failed to load preview text (${response.status})`);
        }

        const text = await response.text();
        const truncated = text.length > 4000;
        return {
            kind,
            text: truncated ? `${text.slice(0, 4000)}\n\n…` : text,
            truncated
        };
    };

    const handleFilePreview = async (file) => {
        setError(null);
        setSidePreviewFile(file);
        const documentKey = getDocumentKey(file);
        setActiveFile(documentKey ? [documentKey] : []);

        if (!file) {
            setSidePreview({ kind: 'error', message: 'No file selected.' });
            return;
        }

        if (Number(file.file_size || 0) > LARGE_FILE_PREVIEW_LIMIT_BYTES) {
            setSidePreview({ kind: 'large' });
            return;
        }

        if (!file.download_url) {
            setSidePreview({ kind: 'generic' });
            return;
        }

        setSidePreview({ kind: 'loading' });

        try {
            const extension = getFileExtension(file);
            const fileIdentifier = file.file_path || file.s3_key || file.key || '';

            if (extension === 'pdf') {
                setSidePreview({ kind: 'pdf', url: file.download_url });
                return;
            }

            if (extension === 'msg' || extension === 'eml') {
                if (!fileIdentifier) {
                    throw new Error('Missing file key for email preview');
                }

                const response = await fetch(`https://api.casedb.co/email-preview.php?fileKey=${encodeURIComponent(fileIdentifier)}&leads=true`);
                const result = await response.json();
                if (!response.ok || !result?.success || !result?.preview) {
                    throw new Error(result?.error || 'Failed to load email preview');
                }

                setSidePreview({ kind: 'email', preview: result.preview });
                return;
            }

            if (IMAGE_EXTENSIONS.includes(extension)) {
                setSidePreview({ kind: 'image', url: file.download_url });
                return;
            }

            if (AUDIO_EXTENSIONS.includes(extension)) {
                setSidePreview({ kind: 'audio', url: file.download_url });
                return;
            }

            if (VIDEO_EXTENSIONS.includes(extension)) {
                setSidePreview({ kind: 'video', url: file.download_url });
                return;
            }

            if (OFFICE_EXTENSIONS.includes(extension)) {
                setSidePreview({ kind: 'office', url: file.office_preview_url || file.download_url, officePreviewUrl: file.office_preview_url, fileType: extension });
                return;
            }

            if (extension === 'ics') {
                setSidePreview(await buildTextPreview(file, 'calendar'));
                return;
            }

            if (TEXT_EXTENSIONS.includes(extension)) {
                setSidePreview(await buildTextPreview(file, 'text'));
                return;
            }

            setSidePreview({ kind: 'generic' });
        } catch (previewError) {
            console.error('Lead document preview error:', previewError);
            setSidePreview({ kind: 'error', message: previewError.message || 'Unknown preview error' });
        }
    };

    const handleFileClick = (file) => {
        if (clickTimer.current) {
            clearTimeout(clickTimer.current);
            clickTimer.current = null;
        }

        clickTimer.current = setTimeout(() => {
            handleFilePreview(file);
            clickTimer.current = null;
        }, 200);
    };

    const handleFileDoubleClick = (file) => {
        if (clickTimer.current) {
            clearTimeout(clickTimer.current);
            clickTimer.current = null;
        }

        handleViewFile(file);
    };

    const renderFileList = () => {
        if (!filteredDocuments || filteredDocuments.length === 0) {
            return (
                <div className="empty-documents">
                    <File size={40} />
                    <p>{metaData?.length ? 'No documents match the current search.' : 'No documents found for this lead'}</p>
                </div>
            );
        }

        return (
            <div className="document-table-container" style={{ flex: sidePreview ? '1' : 'none', minWidth: 0, position: 'relative' }}>
                <table className={`exhibits ${windowWidth <= 900 ? 'hide-author' : ''} ${windowWidth <= 767 ? 'hide-date' : ''} ${windowWidth <= 600 ? 'hide-size' : ''}`}>
                    <thead>
                        <tr>
                            <th className="file-name">
                                <div className="file-select">
                                    <Checkbox
                                        value={visibleSelectableIds.length > 0 && visibleSelectableIds.every((id) => selectedIds.includes(id))}
                                        onChange={(event) => {
                                            event?.stopPropagation?.();
                                            toggleSelectAllVisible();
                                        }}
                                        mini
                                    />
                                </div>
                                <span
                                    className="sortable-header"
                                    onClick={() => handleSort('name')}
                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                                >
                                    Name
                                    {sortColumn === 'name' ? (sortDirection === 'asc' ? <ArrowUpAZ size={14} /> : <ArrowDownAZ size={14} />) : null}
                                </span>
                            </th>
                            <th className="file-date">
                                <span
                                    className="sortable-header"
                                    onClick={() => handleSort('upload_date')}
                                    style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}
                                >
                                    Last Modified
                                    {sortColumn === 'upload_date' ? (sortDirection === 'asc' ? <ArrowUpAZ size={14} /> : <ArrowDownAZ size={14} />) : null}
                                </span>
                            </th>
                            <th className="file-size">
                                <span
                                    className="sortable-header"
                                    onClick={() => handleSort('size')}
                                    style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}
                                >
                                    Size
                                    {sortColumn === 'size' ? (sortDirection === 'asc' ? <ArrowUpAZ size={14} /> : <ArrowDownAZ size={14} />) : null}
                                </span>
                            </th>
                            <th className="file-author">
                                <span
                                    className="sortable-header"
                                    onClick={() => handleSort('type')}
                                    style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px' }}
                                >
                                    Type
                                    {sortColumn === 'type' ? (sortDirection === 'asc' ? <ArrowUpAZ size={14} /> : <ArrowDownAZ size={14} />) : null}
                                </span>
                            </th>
                            <th className="file-actions">...</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDocuments.map((file, index) => {
                            const documentKey = getDocumentKey(file) || `file-${index}`;
                            const menuId = `lead-${documentKey}`;
                            const isActive = activeFile.includes(documentKey);
                            const isSelected = selectedIds.includes(documentKey);
                            const isBusy = actionFileId === documentKey || actionFileId === 'bulk-delete';

                            return (
                                <tr
                                    key={documentKey}
                                    onClick={() => handleFileClick(file)}
                                    onDoubleClick={() => handleFileDoubleClick(file)}
                                    className={`exhibit ${isActive ? 'active-file' : ''} ${more === menuId ? 'menu-active' : ''}`}
                                >
                                    <td className="file-name subtext">
                                        <div className="file-select">
                                            <Checkbox
                                                value={isSelected}
                                                onChange={(event) => {
                                                    event?.stopPropagation?.();
                                                    toggleFileSelection(documentKey);
                                                }}
                                                mini
                                            />
                                        </div>
                                        {getFileIcon(file.file_type, getFileName(file), 16)}
                                        <div className="subtext file-preview-trigger" style={{ flexWrap: 'wrap', paddingLeft: 6 }}>
                                            {getFileName(file)}
                                        </div>
                                    </td>
                                    <td className="file-date subtext" title={file?.upload_date || ''}>{formatDate(file.upload_date)}</td>
                                    <td className="file-size subtext">{formatFileSize(file.file_size)}</td>
                                    <td className="file-author subtext">{file.document_type || file.file_type || getFileExtension(file) || '-'}</td>
                                    <td className="file-actions subtext" onClick={(event) => event.stopPropagation()}>
                                        <span
                                            className="file-actions-dots"
                                            data-menu-index={menuId}
                                            onClick={() => {
                                                const position = calculateMenuPosition(menuId);
                                                setMenuPositionAbove((prev) => ({ ...prev, [menuId]: position.showAbove }));
                                                setMenuPosition((prev) => ({ ...prev, [menuId]: { left: position.left, top: position.top } }));
                                                setMore(more === menuId ? null : menuId);
                                            }}
                                        >...</span>
                                        {more === menuId && typeof document !== 'undefined' && createPortal(
                                            <div
                                                className={`file-actions-menu ${menuPositionAbove[menuId] ? 'position-above' : ''}`}
                                                ref={menuRef}
                                                style={{
                                                    left: menuPosition[menuId]?.left || 0,
                                                    top: menuPosition[menuId]?.top || 0
                                                }}
                                            >
                                                <span className="file-actions-menu-child" onClick={() => { handleFilePreview(file); setMore(null); }}>
                                                    <span>Preview</span><Eye size={16} />
                                                </span>
                                                <span className="file-actions-menu-child" onClick={() => { handleViewFile(file); setMore(null); }}>
                                                    <span>View File</span><ExternalLink size={16} />
                                                </span>
                                                <span className="file-actions-menu-child" onClick={() => { handleDownload(file); setMore(null); }}>
                                                    <span>Download</span><Download size={16} />
                                                </span>
                                                <span className="file-actions-menu-child" onClick={() => { handleEditDocument(file); setMore(null); }}>
                                                    <span>Edit</span><Pencil size={16} />
                                                </span>
                                                <span className="file-actions-menu-child" onClick={() => { handleDeleteDocument(file); setMore(null); }}>
                                                    <span>Delete</span>{isBusy ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                                                </span>
                                            </div>,
                                            document.body
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div
            className={`lead-documents documents-container ${sidePreview ? 'with-preview' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{ position: 'relative' }}
        >
            {isDragOver && (
                <div
                    className="drag-drop-overlay"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px dashed var(--text-color)',
                        borderRadius: '12px',
                        margin: '2px',
                        backdropFilter: 'blur(5px)',
                        pointerEvents: 'none'
                    }}
                >
                    <div
                        style={{
                            color: 'white',
                            fontSize: '24px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                            padding: '15px',
                            borderRadius: '6px',
                            backgroundColor: 'rgba(0, 0, 0, 0.2)'
                        }}
                    >
                        <div style={{ marginBottom: '8px' }}>Drop file(s)/folder to upload</div>
                        <div style={{ fontSize: '18px', fontWeight: 'normal' }}>to this lead</div>
                    </div>
                </div>
            )}
            <div className="document-section">
                <div className="document-filters lead-document-filters">
                    <SearchBar
                        placeholder="Search"
                        expanded={true}
                        setExpanded={() => {}}
                        value={searchQuery}
                        setSearchQuery={setSearchQuery}
                    />

                    <div className="filter-controls lead-document-filter-controls">
                        <select value={sortColumn} onChange={(event) => setSortColumn(event.target.value)}>
                            <option value="upload_date">Sort by date</option>
                            <option value="name">Sort by name</option>
                            <option value="size">Sort by size</option>
                            <option value="type">Sort by type</option>
                        </select>

                        <button
                            type="button"
                            className="form-box alt"
                            onClick={() => setSortDirection((current) => current === 'asc' ? 'desc' : 'asc')}
                            title={`Sort ${sortDirection === 'asc' ? 'descending' : 'ascending'}`}
                        >
                            {sortDirection === 'asc' ? <ArrowDownAZ size={16} /> : <ArrowUpAZ size={16} />}
                        </button>

                        <button type="button" className="form-box alt" onClick={handleRefresh} title="Refresh documents">
                            <RefreshCw size={16} />
                        </button>
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileInputChange}
                        style={{ display: 'none' }}
                    />
                    <button
                        className="action quaternary small-text upload-files-btn"
                        onClick={() => fileInputRef.current?.click()}
                        onDragEnter={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            event.currentTarget.classList.add('drag-over');
                        }}
                        onDragOver={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            event.currentTarget.classList.add('drag-over');
                        }}
                        onDragLeave={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            event.currentTarget.classList.remove('drag-over');
                        }}
                        onDrop={handleButtonDrop}
                        disabled={uploading}
                        style={{ transition: 'all 0.3s ease', position: 'relative' }}
                    >
                        {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                        {uploading ? 'Uploading...' : 'Upload Files'}
                    </button>

                    {selectedIds.length > 0 && (
                        <>
                            <button type="button" className="form-box alt" onClick={handleBulkDownload} title="Download selected">
                                <Download size={16} />
                            </button>
                            <button type="button" className="form-box alt" onClick={handleBulkDelete} title="Delete selected">
                                <Trash2 size={16} />
                            </button>
                            <button type="button" className="form-box alt" onClick={clearSelection} title="Clear selection">
                                <X size={16} />
                            </button>
                        </>
                    )}
                </div>

                {error && <div className="upload-error">{error}</div>}

                <div className="document-content" style={{ display: 'flex', flexDirection: sidePreview ? 'row' : 'column', alignItems: 'flex-start', overflow: 'visible' }}>
                        {renderFileList()}

                    {sidePreview && sidePreviewFile && (
                        <div className="file-preview-panel" style={{
                            position: windowWidth <= 767 ? 'static' : 'sticky',
                            top: windowWidth <= 767 ? undefined : '12px',
                            alignSelf: 'flex-start',
                            width: windowWidth <= 767 ? '100%' : undefined,
                            maxHeight: windowWidth <= 767 ? 'none' : 'calc(100vh - 140px)',
                            overflow: windowWidth <= 767 ? 'visible' : 'hidden'
                        }}>
                            <div className="preview-panel-header">
                                <h5>{getFileName(sidePreviewFile)}</h5>
                                <button
                                    className="close-preview-panel"
                                    onClick={() => {
                                        setSidePreview(null);
                                        setSidePreviewFile(null);
                                        setActiveFile([]);
                                    }}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="preview-panel-content" style={{ maxHeight: windowWidth <= 767 ? 'none' : 'calc(100vh - 200px)', overflowY: windowWidth <= 767 ? 'visible' : 'auto' }}>
                                <LeadPreviewPanel
                                    previewState={sidePreview}
                                    file={sidePreviewFile}
                                    onView={handleViewFile}
                                    onDownload={handleDownload}
                                    onDelete={handleDeleteDocument}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
